import { useSearchParams } from "react-router-dom";
import { XMarkIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { useMobileNav } from "../../store/context/MobileNavContext.js";
import { MIN_SEARCH_CHARS } from "../../services/lib/constants.js";
import { useState, useMemo, useCallback } from "react";
import {
  useSearchVideosMutation,
  useGetRecentViewsQuery,
  // opzionale: se hai una mutation per salvare lato server
  // useAddRecentViewMutation,
} from "../../services/modulesuser/videoListApi";

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get("q") || "";

  const [inputText, setInputText] = useState(activeCategory);
  const [isFocused, setIsFocused] = useState(false);

  const { isMenuOpen, closeMenu } = useMobileNav();

  const [searchVideos] = useSearchVideosMutation();
  const { data: recentSearches = [], isLoading: loadingRecents } = useGetRecentViewsQuery();
  // opzionale:
  // const [addRecent] = useAddRecentViewMutation?.() ?? [];

  // 🔁 Solo bucket ottimistico locale (stringhe). I dati server non vengono copiati nello stato.
  const [optimisticRecents, setOptimisticRecents] = useState([]); // es: ["Matrix", "Oppenheimer"]

  // helper per estrarre la label visualizzata
  const getLabel = (t) => (t?.title || t?.query || t || "").toString().trim();

  // 🔗 Merge view: ottimistici prima, poi server, senza duplicati – max 10
  const displayRecents = useMemo(() => {
    const serverList = Array.isArray(recentSearches)
      ? recentSearches.map(getLabel)
      : (recentSearches?.items || []).map(getLabel);

    const seen = new Set();
    const merged = [];

    // prima i locali (optimistic)
    for (const s of optimisticRecents) {
      const k = s.toLowerCase();
      if (!k || seen.has(k)) continue;
      seen.add(k);
      merged.push({ id: `opt_${k}`, title: s });
    }
    // poi il server
    for (const s of serverList) {
      const k = s.toLowerCase();
      if (!k || seen.has(k)) continue;
      seen.add(k);
      merged.push({ id: `srv_${k}`, title: s });
    }

    return merged.slice(0, 10);
  }, [optimisticRecents, recentSearches]);

  // inserisce/porta in cima un termine nel bucket ottimistico
  const pushRecentOptimistic = (label) => {
    const s = (label || "").trim();
    if (!s) return;
    setOptimisticRecents((prev) => {
      const noDup = prev.filter((x) => x.toLowerCase() !== s.toLowerCase());
      return [s, ...noDup].slice(0, 10);
    });

    // opzionale: salva lato server (se hai la mutation)
    // addRecent({ query: s }).catch(() => {
    //   // rollback opzionale in caso di errore
    //   setOptimisticRecents((prev) => prev.filter((x) => x.toLowerCase() !== s.toLowerCase()));
    // });
  };

  // avvia la ricerca
  const handleSearch = useCallback(() => {
    const q = inputText.trim();
    if (q.length > MIN_SEARCH_CHARS) {
      searchVideos({ title: q, page: 1 });
      setSearchParams({ q, page: "1" });
      if (isMenuOpen) closeMenu();
      // ✅ aggiungi SUBITO ai recenti locali
      pushRecentOptimistic(q);
    }
  }, [inputText, isMenuOpen, searchVideos, setSearchParams, closeMenu]);

  // seleziona un recente
  const selectRecent = (termOrLabel) => {
    const searchTerm = typeof termOrLabel === "string" ? termOrLabel : getLabel(termOrLabel);
    setSearchParams({ q: searchTerm, page: "1" });
    setInputText(searchTerm);
    window.scrollTo({ top: 0, behavior: "smooth" });
    // porta il selezionato in cima anche localmente
    pushRecentOptimistic(searchTerm);
  };

  const clearSearch = () => {
    setInputText("");
    setIsFocused(false);
  };

  return (
    <aside className="w-100 pt-10 text-white flex-shrink-0">
      {/* Input */}
      <div className="relative mb-6 flex items-center">
        <input
          type="text"
          className="w-full max-w-[22rem] md:max-w-[24rem] xl:max-w-[28rem]
             rounded-full outline-none text-black text-[1.1rem] xl:text-[1.2rem]
             border border-stone-50 shadow-md transition-all duration-200
             px-5 py-3 pr-12"
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setTimeout(() => setIsFocused(false), 150)}
          onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          placeholder="Cerca per film..."
          aria-label="Cerca per film"
        />

        {/* invio ricerca */}
        <button
          type="button"
          onClick={handleSearch}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-700 hover:text-black"
          aria-label="Cerca"
          title="Cerca"
        >
          <MagnifyingGlassIcon className="w-6 h-6" />
        </button>

        {/* clear */}
        {inputText.length > 0 && (
          <button
            type="button"
            onClick={clearSearch}
            className="absolute right-11 top-1/2 -translate-y-1/2 text-gray-700"
            aria-label="Pulisci"
            title="Pulisci"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Recenti */}
      <h2 className="w-full mb-4 text-4xl font-bold">Ricerche Recenti</h2>
      {loadingRecents ? (
        <p className="text-gray-500 text-sm">Caricamento...</p>
      ) : displayRecents.length === 0 ? (
        <p className="text-gray-500 text-sm">Nessuna ricerca recente</p>
      ) : (
        <ul className="space-y-2">
          {displayRecents.map(({ id, title }) => (
            <li key={id}>
              <button
                onClick={() => selectRecent(title)}
                className="block w-full text-left px-2 py-1 rounded hover:bg-gray-800 text-gray-300 transition-colors"
                title={title}
              >
                {title}
              </button>
            </li>
          ))}
        </ul>
      )}
    </aside>
  );
}
